import { cn } from '@/lib/cn';
import { formatProgress } from '@/lib/format';

export interface ProgressBarProps {
  /** 0-1. `FeatureRollup.progress` is already in this range. */
  value: number;
  label?: string;
  /** A token colour for the fill. Defaults to the completed lane. */
  tone?: string;
  /** Shows the percentage to the right of the bar. */
  showValue?: boolean;
  size?: 'sm' | 'md';
  /**
   * Blocked work inside what the bar measures. Renders a hatched segment at
   * the end of the fill so "80% done, some of it stuck" is one glance rather
   * than two numbers.
   */
  blockedFraction?: number;
  className?: string;
}

export function ProgressBar({
  value,
  label,
  tone,
  showValue = false,
  size = 'md',
  blockedFraction = 0,
  className,
}: ProgressBarProps) {
  const clamped = Math.min(Math.max(value, 0), 1);
  const blocked = Math.min(Math.max(blockedFraction, 0), 1 - clamped);

  return (
    <div className={cn('min-w-0', className)}>
      {label || showValue ? (
        <div className="mb-1 flex items-baseline justify-between gap-2">
          {label ? <span className="text-micro text-muted">{label}</span> : <span />}
          {showValue ? (
            <span className="font-mono text-micro text-ink tnum">{formatProgress(clamped)}</span>
          ) : null}
        </div>
      ) : null}

      <div
        role="progressbar"
        aria-valuenow={Math.round(clamped * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label ?? 'Progress'}
        className={cn(
          'flex w-full overflow-hidden rounded-full bg-sunken',
          size === 'sm' ? 'h-1.5' : 'h-2',
        )}
      >
        <div
          className="h-full transition-[width] duration-500 ease-out"
          style={{ width: `${clamped * 100}%`, backgroundColor: tone ?? 'var(--lane-completed)' }}
        />
        {blocked > 0 ? (
          <div
            className="hatch-blocked h-full border-l border-danger/40"
            style={{ width: `${blocked * 100}%` }}
            title="Blocked"
          />
        ) : null}
      </div>
    </div>
  );
}
